import moment from 'moment';

import captureStorageError from '../../../../utils/localStorage';

const BREVO_AUTH_STORAGE_KEY = 'massTextOutreach_brevoAuthCookie';

export const BREVO_AUTH_COOKIE_HEADER = 'x-brevo-auth-cookie';

export const BREVO_AUTH_DEFAULT_EXPIRY_DAYS = 14;

export const BREVO_SESSION_UNAVAILABLE_STATUS = 424;

export class BrevoSessionUnavailableError extends Error {
  constructor() {
    super('BREVO_SESSION_UNAVAILABLE');
    this.name = 'BrevoSessionUnavailableError';
  }
}

export const throwIfBrevoSessionUnavailable = (response) => {
  if (response.status === BREVO_SESSION_UNAVAILABLE_STATUS) {
    throw new BrevoSessionUnavailableError();
  }
};

const defaultExpiresAt = () => moment().add(BREVO_AUTH_DEFAULT_EXPIRY_DAYS, 'days').toISOString();

const writeRawSession = (session) => {
  try {
    localStorage.setItem(BREVO_AUTH_STORAGE_KEY, JSON.stringify(session));
    return session;
  } catch (e) {
    captureStorageError('setItem', BREVO_AUTH_STORAGE_KEY, e, 'MassTextOutreach brevoAuth');
    return null;
  }
};

const readRawSession = () => {
  try {
    const raw = localStorage.getItem(BREVO_AUTH_STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && typeof parsed.value === 'string') {
        return { value: parsed.value, expiresAt: parsed.expiresAt || null };
      }
      return null;
    } catch (_) {
      const migrated = { value: raw, expiresAt: defaultExpiresAt() };
      writeRawSession(migrated);
      return migrated;
    }
  } catch (e) {
    captureStorageError('getItem', BREVO_AUTH_STORAGE_KEY, e, 'MassTextOutreach brevoAuth');
    return null;
  }
};

export const getBrevoAuthSession = () => readRawSession();

export const isSessionActive = (session) => {
  if (!session || !session.value) return false;
  if (!session.expiresAt) return true;
  const expiresMs = new Date(session.expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return true;
  return expiresMs > Date.now();
};

export const getBrevoAuthCookie = () => {
  const session = readRawSession();
  return isSessionActive(session) ? session.value : '';
};

export const saveBrevoAuthSession = ({ value, expiresAt }) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return null;
  const effectiveExpiresAt = expiresAt || defaultExpiresAt();
  return writeRawSession({ value: trimmed, expiresAt: effectiveExpiresAt });
};

export const clearBrevoAuthSession = () => {
  try {
    localStorage.removeItem(BREVO_AUTH_STORAGE_KEY);
  } catch (e) {
    captureStorageError('removeItem', BREVO_AUTH_STORAGE_KEY, e, 'MassTextOutreach brevoAuth');
  }
};
